import { clipTextFromTag } from './clipSegmentUtils';
import { formatClipTimestamp } from './clipTimeUtils';

const START_FULL = '00:00:00.00';

/** One URL per line or comma-separated. */
export function parseBulkVideoLinks(raw) {
  return (raw || '')
    .split(/[\n,]+/)
    .map(s => s.trim())
    .filter(s => /^https?:\/\//i.test(s));
}

export function collectClipTagsInSection(clipsTree) {
  const used = new Set();
  (clipsTree || []).forEach(charClip => {
    (charClip.videos || []).forEach(video => {
      (video.vidclips || []).forEach(clip => {
        const tag = (clip.tag || '').trim();
        if (tag) used.add(tag);
      });
    });
  });
  return used;
}

export function allocateImportTag(prefix, index, usedTags) {
  const base = (prefix || 'clip').trim() || 'clip';
  let n = index;
  let tag = `${base}${n}`;
  while (usedTags.has(tag)) {
    n += 1;
    tag = `${base}${n}`;
  }
  usedTags.add(tag);
  return tag;
}

/**
 * Build Videos-tab entries + timeline segments for resolved imports.
 * @param {Array<{ url, title, duration }>} items
 */
export function buildBulkImportBundle(items, character, tagPrefix, usedTags) {
  const videos = [];
  const segments = [];
  const clipTagToggleKeys = [];

  items.forEach((item, idx) => {
    const tag = allocateImportTag(tagPrefix, idx + 1, usedTags);
    const endTime = formatClipTimestamp(item.duration);
    if (!endTime) return;

    videos.push({
      url: item.url,
      title: item.title || `Import ${idx + 1}`,
      vidclips: [{
        startTime: START_FULL,
        endTime,
        mute: true,
        tag,
      }],
    });

    segments.push({
      character,
      text: clipTextFromTag(tag),
      tag,
    });

    clipTagToggleKeys.push(tag);
  });

  return { videos, segments, clipTagToggleKeys };
}

/** Merge new videos into section_videos sidebar structure. */
export function mergeSectionVideos(sectionVideos, character, newVideos) {
  const list = [...(sectionVideos || [])];
  let block = list.find(v => v.Character === character);
  if (!block) {
    list.push({ Character: character, videos: [...newVideos] });
    return list;
  }
  const merged = [...(block.videos || [])];
  newVideos.forEach(v => {
    if (!merged.some(x => x.url === v.url)) merged.push(v);
  });
  return list.map(v => (v.Character === character ? { ...v, videos: merged } : v));
}

/** Merge into selectedData.clips tree for one character. */
export function mergeClipsTree(clips, character, newVideos) {
  const tree = [...(clips || [])];
  const charIdx = tree.findIndex(c => c.characterName === character);
  if (charIdx === -1) {
    tree.push({ characterName: character, videos: [...newVideos] });
    return tree;
  }
  const charData = { ...tree[charIdx] };
  const vids = [...(charData.videos || [])];
  newVideos.forEach(v => {
    const i = vids.findIndex(x => x.url === v.url);
    if (i === -1) vids.push(v);
    else vids[i] = v;
  });
  charData.videos = vids;
  tree[charIdx] = charData;
  return tree;
}

/** Insert imported clip segments at end, after anchor, or before anchor. */
export function insertSegmentsAt(existingSegments, newSegments, placement) {
  const segs = [...(existingSegments || [])];
  const list = [...(newSegments || [])];
  if (!list.length) return segs;

  const mode = placement?.mode || 'end';
  const anchor = Number.isFinite(placement?.index) ? placement.index : segs.length - 1;

  let at = segs.length;
  if (mode === 'after') {
    at = Math.min(segs.length, Math.max(0, anchor + 1));
  } else if (mode === 'before') {
    at = Math.min(segs.length, Math.max(0, anchor));
  }

  segs.splice(at, 0, ...list);
  return segs;
}

export function segmentPlacementLabel(index, segment, total) {
  const n = index + 1;
  const text = (segment?.text || '').trim().replace(/\s+/g, ' ');
  const preview = text.length > 36 ? `${text.slice(0, 36)}…` : text;
  if (preview) return `#${n} — ${preview}`;
  return `Segment ${n} of ${total}`;
}
